import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  clampPage,
  filtersPatch,
  fromDateParam,
  isUrlStateDirty,
  LIST_VALIDATORS,
  listReturnPath,
  listReturnState,
  ListState,
  pickFilters,
  readUrlState,
  resetPatch,
  toDateParam,
  UrlSchema,
  UrlValidators,
  writeUrlState,
} from '@/utils/urlListState';

type Patch<T> = Partial<T> | ((prev: T) => Partial<T>);

/**
 * List state that lives in the URL query instead of `useState`, so a refresh
 * keeps the user's search, filters, sort and page. The URL is the only copy:
 * there is no local state to drift out of sync with it.
 *
 * `defaults` and `validators` are read once per URL change; pass module-level
 * constants. Every write replaces the history entry, so Back leaves the list
 * rather than undoing filters one by one.
 */
export const useUrlListState = <T extends UrlSchema>(
  defaults: T,
  validators?: UrlValidators<T>
) => {
  const [params, setParams] = useSearchParams();
  const search = params.toString();

  // React Router's functional setSearchParams reads the URL as it was when the
  // handler started, so `set({ q }); set({ page: 1 })` in one handler would
  // drop the search. Writes chain on the latest pending value instead.
  const pending = useRef<{ base: string; next: URLSearchParams } | null>(null);
  const latest = useRef(params);
  latest.current = params;
  // The query this hook last wrote, to tell its own writes from the URL
  // changing under it (a sidebar link, Back/Forward).
  const written = useRef<string | null>(null);

  const config = useRef({ defaults, validators });
  config.current = { defaults, validators };

  const state = useMemo(
    () =>
      readUrlState(
        new URLSearchParams(search),
        config.current.defaults,
        config.current.validators
      ),
    [search]
  );

  const set = useCallback(
    (patch: Patch<T>) => {
      const current = latest.current.toString();
      const base =
        pending.current?.base === current
          ? pending.current.next
          : latest.current;
      const { defaults: d, validators: v } = config.current;
      const resolved =
        typeof patch === 'function' ? patch(readUrlState(base, d, v)) : patch;
      const next = writeUrlState(base, resolved, d);
      if (next.toString() === base.toString()) return;
      pending.current = { base: current, next };
      written.current = next.toString();
      setParams(next, { replace: true });
    },
    [setParams]
  );

  /** Everything back to its default except the `keep` keys. */
  const reset = useCallback(
    (keep: (keyof T)[] = []) => set(resetPatch(config.current.defaults, keep)),
    [set]
  );

  /** Whether anything outside `ignore` differs from the defaults. */
  const isDirty = useCallback(
    (ignore: (keyof T)[] = []) =>
      isUrlStateDirty(state, config.current.defaults, ignore),
    [state]
  );

  /** Whether the URL as it stands is the one this hook last wrote. */
  const wroteCurrent = useCallback(
    () => latest.current.toString() === written.current,
    []
  );

  return { state, set, reset, isDirty, wroteCurrent };
};

/**
 * `useUrlListState` shaped like the `useState` calls the list pages had, so a
 * page swaps its state for this without rewriting its fetch code. Every change
 * except paging sends the list back to page 1, as each page already did.
 *
 * `filterKeys` are the UniversalFilter keys; pass a module-level constant.
 */
export const useListQuery = <T extends ListState>(
  defaults: T,
  filterKeys: readonly (keyof T & string)[],
  validators?: UrlValidators<T>
) => {
  const list = useUrlListState(defaults, {
    ...(LIST_VALIDATORS as UrlValidators<T>),
    ...validators,
  });
  const { state, set, reset, isDirty, wroteCurrent } = list;
  const keys = useRef(filterKeys);

  // Keyed on content, not on `state`: paging rebuilds `state`, and a page
  // whose effects depend on the filters (badge counts, CSV params) must not
  // refetch just because the page changed.
  const filtersKey = JSON.stringify(pickFilters(state, keys.current));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeFilters = useMemo(() => JSON.parse(filtersKey), [filtersKey]);
  const dateRange = useMemo(
    () => ({ from: fromDateParam(state.from), to: fromDateParam(state.to) }),
    [state.from, state.to]
  );

  // DateRangeFilter seeds itself once, on mount, and the search box holds
  // typed-but-unapplied text. Both follow the URL when it is reset or changes
  // under the list; `controlsKey` remounts the picker with the new range.
  const [controlsKey, setControlsKey] = useState(0);
  const [searchInput, setSearchInput] = useState(state.q);
  const seen = useRef(state);
  useEffect(() => {
    if (seen.current === state) return;
    seen.current = state;
    if (wroteCurrent()) return;
    setSearchInput(state.q);
    setControlsKey((k) => k + 1);
  }, [state, wroteCurrent]);

  const datePickerSeed = useMemo(
    () => ({
      initialRange: dateRange,
      initialTimeRange: (dateRange.from || dateRange.to
        ? 'custom'
        : 'all-time') as 'custom' | 'all-time',
    }),
    [dateRange]
  );

  const setCurrentPage = useCallback(
    (page: number) => set({ page } as Partial<T>),
    [set]
  );
  const setSearchTerm = useCallback(
    (q: string) => set({ q, page: 1 } as Partial<T>),
    [set]
  );
  const setFilters = useCallback(
    (filters: Record<string, unknown>) =>
      set({
        ...filtersPatch(filters, keys.current, defaults),
        page: 1,
      } as Partial<T>),
    // `defaults` is a module constant by contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [set]
  );
  const setDateRange = useCallback(
    (range: { from: Date | null; to: Date | null }) =>
      set({
        from: toDateParam(range.from),
        to: toDateParam(range.to),
        page: 1,
      } as Partial<T>),
    [set]
  );
  const setSort = useCallback(
    (sort: string, dir: 'asc' | 'desc' | null) =>
      set({ sort, dir, page: 1 } as Partial<T>),
    [set]
  );
  const setPageSize = useCallback(
    (size: number) => set({ size, page: 1 } as Partial<T>),
    [set]
  );

  /**
   * Everything back to its default except the page size and the `keep` keys.
   * Paging alone does not count as a filter: a list on page 3 with nothing
   * narrowed shows no Reset button.
   */
  const hasFilters = useCallback(
    (keep: (keyof T)[] = []) => isDirty(['size', 'page', ...keep]),
    [isDirty]
  );
  const resetFilters = useCallback(
    (keep: (keyof T)[] = []) => {
      reset(['size', ...keep]);
      setSearchInput('');
      setControlsKey((k) => k + 1);
    },
    [reset]
  );

  /**
   * Pulls `page` back inside the list once its total is known. A zero total is
   * skipped: it is also what a list reports before its first fetch lands, and
   * an empty list is empty on any page.
   */
  const clampToTotal = useCallback(
    (total: number) => {
      if (total <= 0) return;
      const clamped = clampPage(state.page, total, state.size);
      if (clamped !== state.page) set({ page: clamped } as Partial<T>);
    },
    [state.page, state.size, set]
  );

  return {
    ...list,
    searchTerm: state.q,
    currentPage: state.page,
    pageSize: state.size,
    sortField: state.sort,
    sortDirection: state.dir as 'asc' | 'desc' | null,
    activeFilters,
    dateRange,
    datePickerSeed,
    controlsKey,
    searchInput,
    setSearchInput,
    setCurrentPage,
    setSearchTerm,
    setFilters,
    setDateRange,
    setSort,
    setPageSize,
    hasFilters,
    resetFilters,
    clampToTotal,
  };
};

/** Clamps the page once a list's fetch has landed with its total. */
export const usePageClamp = (
  clampToTotal: (total: number) => void,
  total: number,
  loading: boolean
) => {
  useEffect(() => {
    if (!loading) clampToTotal(total);
  }, [clampToTotal, total, loading]);
};

/**
 * Opens a detail page from a list, handing over the list URL (query included)
 * so the detail page's Back button can return to the same filtered list.
 */
export const useNavigateFromList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (path: string) => navigate(path, { state: listReturnState(location) }),
    [navigate, location]
  );
};

/** Where a detail page's Back button goes; `fallback` is the plain list path. */
export const useListReturnPath = (fallback: string): string => {
  const location = useLocation();
  return listReturnPath(location.state, fallback);
};

/**
 * A page's tab, in the URL as `tab`, for pages whose tabs are separate lists.
 * Switching tabs clears the rest of the query: one tab's filters do not carry
 * over to another. The default tab stays out of the URL.
 */
export const useUrlTab = <K extends string>(
  tabs: readonly K[],
  defaultTab: K
): [K, (tab: K) => void] => {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = tabs.includes(raw as K) ? (raw as K) : defaultTab;
  const setTab = useCallback(
    (next: K) => {
      if (next === tab) return;
      setParams(next === defaultTab ? {} : { tab: next }, { replace: true });
    },
    [tab, defaultTab, setParams]
  );
  return [tab, setTab];
};
